import React, { useState, useEffect, useCallback, useMemo } from 'react';
import LinkSection from './LinkSection';
import VideoPopup from './VideoPopup';
import { sound } from '../utils/sound';

interface ContentLink {
  title: string;
  video_url: string;
  module?: string;
  sku?: string;
  id?: number;
}

interface VideoModule {
  name: string;
  videos: ContentLink[];
}

interface LinkInventoryProps {
  videos: ContentLink[] | null;
  onClose: () => void;
  loading: boolean;
}

const LinkInventory: React.FC<LinkInventoryProps> = ({ videos, onClose, loading }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [videoPopupUrl, setVideoPopupUrl] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    if (isClosing) return; // Prevent multiple close calls

    sound.clickClose();
    setIsClosing(true);

    // Wait for animation to complete before closing
    setTimeout(() => {
      onClose();
    }, 350); // Match the animation duration
  }, [isClosing, onClose]);

  // Handle Escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isClosing) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isClosing, handleClose]);

  // Process videos into modules (memoized to avoid recalculation)
  const videoModules = useMemo(() => {
    if (!videos || videos.length === 0) return [];

    // Group videos by module
    const moduleMap = new Map<string, ContentLink[]>();

    videos.forEach((video: ContentLink) => {
      const moduleName = video.module || 'Other';
      if (!moduleMap.has(moduleName)) {
        moduleMap.set(moduleName, []);
      }
      moduleMap.get(moduleName)!.push(video);
    });

    // Sort modules with alphanumeric-aware (natural) sorting
    const sortedModules: VideoModule[] = Array.from(moduleMap.entries())
      .sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
      .map(([name, videos]) => ({
        name,
        // Sort videos within each module by sku (if exists), otherwise by id
        videos: videos.sort((a, b) => {
          const skuA = a.sku || (a.id !== undefined ? String(a.id) : '');
          const skuB = b.sku || (b.id !== undefined ? String(b.id) : '');
          return skuA.localeCompare(skuB, undefined, { numeric: true, sensitivity: 'base' });
        })
      }));

    return sortedModules;
  }, [videos]);

  const handleVideoClick = (url: string) => {
    setVideoPopupUrl(url);
  };

  return (
    <>
      <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
        {!loading && videos && (
          <div className={`link-inventory-content ${isClosing ? 'closing' : ''} link-inventory-content-loaded`} onClick={(e) => e.stopPropagation()}>
            <div className="link-inventory-header">
              <h2>
                <img src="/knowledge.svg" alt="" className="header-icon" />
                Knowledge Hub
              </h2>
              <button className="link-inventory-close-button" onClick={handleClose}>
                ✕
              </button>
            </div>
            <div className="link-inventory-body">
              {videoModules.length > 0 ? (
                <>
                  {videoModules.map((module, index) => (
                    <LinkSection
                      key={index}
                      title={module.name}
                      videos={module.videos}
                      onVideoClick={handleVideoClick}
                    />
                  ))}
                </>
              ) : (
                <div className="no-videos-message">
                  No videos available yet.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {videoPopupUrl && (
        <VideoPopup
          videoUrl={videoPopupUrl}
          onClose={() => setVideoPopupUrl(null)}
        />
      )}
    </>
  );
};

export default LinkInventory;
